/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import {ErrorType, generateUUID, nullify, NumberString, ResponseData, roundTo, roundToNumber} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useState} from 'react';
import Invoice, {InvoiceFields} from '../../../models/invoiceModel';
import {trackPlatformAction} from '../../../util/analytics';
import {EditMode, InvoiceType} from '../../../util/interfaces';
import {AppContext} from '../../misc/appContext';
import {createErrorMessage, ensureNegative} from '../../../util/utils';
import OrderItem, {CreditItem} from '../../../models/orderItemModel';
import CreditInvoiceData from './creditInvoiceData';
import {
    createCreditInvoice,
    CreditInvoiceItem,
    updateCreditInvoice,
    UpdateCreditInvoiceItem
} from '../../../util/requestSender';
import {INVOICES_ROUTE} from '../../../util/constants';
import {useNavigate} from 'react-router';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

function findCreditItem(creditItems: Array<CreditItem>, orderItemID: string): CreditItem | null {
    return creditItems.find((item: CreditItem) => item.orderItem?.id === orderItemID) ?? null;
}

export default function CreditInvoiceDataController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [creditInvoice, setCreditInvoice] = useState(new Invoice({invoice_type: InvoiceType.CREDIT}));
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState(new Array<CreditItem>());
    const [errors, setErrors] = useState<Record<string, ErrorType>>({});
    const [mode, setMode] = useState(EditMode.NEW);
    const navigate = useNavigate();

    const onChangeField = (key: keyof InvoiceFields) => (value: string) => {
        const invoice = creditInvoice.clone();
        invoice.setField(key, value);
        setCreditInvoice(invoice);
    };

    function onChangePriceIncl(orderItemID: string) {
        return function (priceIncl: NumberString) {
            const creditItems = items.slice();
            const creditItem = findCreditItem(creditItems, orderItemID);
            if (!creditItem) return;
            const orderItem = creditItem.orderItem!;
            const orderItemPrice = orderItem.piecePrice;
            if (roundToNumber(priceIncl.numberValue, 2) > roundToNumber(orderItemPrice, 2)) {
                setError(
                    orderItemID,
                    t('invoice.credit.errorPriceTooHigh', 'De prijs kan niet hoger zijn dan de originele prijs')
                );
                return;
            }
            onResetError(orderItemID)();
            if (!priceIncl.numberValue) {
                creditItem.quantity = new NumberString(0);
            } else {
                creditItem.priceIncl = new NumberString(priceIncl);
            }
            setItems(creditItems);
        };
    }

    function onChangePercentage(orderItemID: string) {
        return function (value: NumberString) {
            if (value.numberValue > 100) {
                setError(orderItemID, 'Het percentage kan niet hoger dan 100% zijn');
                return;
            }
            const creditItems: CreditItem[] = items.slice();
            const creditItem = findCreditItem(creditItems, orderItemID);
            if (!creditItem) return;
            onResetError(orderItemID)();
            creditItem.percentage = value;
            setItems(creditItems);
        };
    }

    function onChangeQuantity(orderItemID: string) {
        return function (value: NumberString) {
            const creditItems = items.slice();
            const creditItem = findCreditItem(creditItems, orderItemID);
            if (!creditItem) return;
            if (value.numberValue > creditItem.orderItem!.quantity) {
                setError(
                    orderItemID,
                    t('invoice.credit.errorQuantityTooHigh', 'Het aantal kan niet hoger zijn dan het originele aantal')
                );
                return;
            }
            creditItem.quantity = value;
            onResetError(orderItemID)();
            setItems(creditItems);
            return;
        };
    }

    function onSubmit() {
        if (validateValues()) {
            setLoading(true);
            if (mode === EditMode.NEW) onSave();
            else onUpdate();
        }
    }

    function onSave() {
        if (!account) return;
        const creditItems = prepareSaveItems();
        createCreditInvoice({
            data: {
                account_id: account.id,
                invoice_id: creditInvoice.id,
                items: creditItems,
                coc_number: nullify(creditInvoice.cocNumber),
                vat_number: nullify(creditInvoice.vatNumber)
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    trackPlatformAction('invoices', 'create_credit');
                    showSnackbar(t('invoice.credit.createdSuccess', 'De credifactuur is aangemaakt'));
                    const invoice = new Invoice(data.credit_invoice);
                    navigate(`/factuur-details?id=${invoice.id}`);
                } else
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t('invoice.credit.createFailed', 'Het aanmaken van de creditfactuur is niet gelukt.')
                            ),
                        t('invoice.credit.createFailedTitle', 'Aanmaken mislukt')
                    );
            }
        });
    }

    function onUpdate() {
        if (!account) return;
        const updateData = prepareUpdateItems();
        const {creditItems, delete_item_ids, add_items} = updateData;
        updateCreditInvoice({
            data: {
                account_id: account.id,
                credit_invoice_id: creditInvoice.id,
                items: creditItems,
                delete_item_ids,
                add_items,
                coc_number: nullify(creditInvoice.cocNumber),
                vat_number: nullify(creditInvoice.vatNumber)
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showSnackbar(t('invoice.credit.updatedSuccess', 'De credifactuur is gewijzigd'));
                    const invoice = new Invoice(data.credit_invoice);
                    navigate(`/factuur-details?id=${invoice.id}`);
                } else
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t('invoice.credit.updateFailed', 'Het wijzigen van de creditfactuur is niet gelukt.')
                            ),
                        t('invoice.credit.updateFailedTitle', 'Wijzigen mislukt')
                    );
            }
        });
    }

    function onClose() {
        history.back();
    }

    const onResetError = (id: string) => () => {
        const errs = Object.assign({}, errors);
        if (Object.hasOwn(errs, id)) {
            errs[id] = null;
            setErrors(errs);
        }
    };

    function setError(itemId: string, error: string) {
        const errs = Object.assign({}, errors);
        errs[itemId] = error;
        setErrors(errs);
    }

    function validateValues(): boolean {
        let valid = true;
        for (const item of items) {
            let error: ErrorType = null;
            const {submitPrice, quantity, subTotalIncl, orderItem} = item;
            if (orderItem) {
                if (!quantity.numberValue && !submitPrice)
                    // Empty item, will be deleted so we don't have to validate
                    continue;

                const roundedPrice = roundToNumber(submitPrice, 2);
                const originalPrice = roundToNumber(orderItem.price, 2);
                const originalQuantity = orderItem.quantity;

                if (!quantity) {
                    error = t('invoice.credit.errorQuantityMin', 'Het aantal moet minimaal 1 zijn');
                } else if (!subTotalIncl) {
                    error = t('invoice.credit.errorFillVatOrPrice', 'Vul minimaal het BTW bedrag of de prijs in in');
                } else if (quantity.numberValue > originalQuantity) {
                    error = t(
                        'invoice.credit.errorQuantityTooHigh',
                        'Het aantal kan niet hoger zijn dan het originele aantal'
                    );
                } else if (roundedPrice > originalPrice) {
                    error = t(
                        'invoice.credit.errorPriceTooHigh',
                        'De prijs kan niet hoger zijn dan de originele prijs'
                    );
                }
                if (error) {
                    valid = false;
                    setError(orderItem.id, error);
                }
            }
        }
        return valid;
    }

    function prepareSaveItems(): Array<CreditInvoiceItem> {
        const creditItems: Array<CreditInvoiceItem> = [];
        for (const item of items) {
            if (item.quantity && item.subTotalIncl) {
                const {orderItem} = item;
                if (orderItem) {
                    creditItems.push({
                        ean: orderItem.ean,
                        item_id: orderItem.itemId,
                        order_item_id: orderItem.id,
                        title: item.title.length ? item.title : orderItem.title,
                        quantity: item.quantity.numberValue,
                        price: ensureNegative(item.submitPrice),
                        reference: orderItem.reference
                    });
                }
            }
        }
        return creditItems;
    }

    function prepareUpdateItems(): {
        creditItems: UpdateCreditInvoiceItem[];
        delete_item_ids: string[];
        add_items: CreditInvoiceItem[];
    } {
        const creditItems: Array<UpdateCreditInvoiceItem> = [];
        const delete_item_ids: Array<string> = [];
        const add_items: Array<CreditInvoiceItem> = [];
        for (const item of items) {
            if (item.quantity && item.subTotalIncl) {
                if (item.id.endsWith('-add')) {
                    const orderItem = item.orderItem!;
                    add_items.push({
                        ean: orderItem.ean,
                        item_id: orderItem.itemId,
                        order_item_id: orderItem.id,
                        title: orderItem.title,
                        quantity: item.quantity.numberValue,
                        price: item.submitPrice * -1,
                        reference: orderItem.reference
                    });
                } else
                    creditItems.push({
                        id: item.id,
                        price: item.submitPrice * -1,
                        quantity: item.quantity.numberValue
                    });
            } else if (!item.id.endsWith('-add')) delete_item_ids.push(item.id);
        }
        return {creditItems, delete_item_ids, add_items};
    }

    function initialize() {
        const invoiceId = new URLSearchParams(location.search).get('invoice-id');
        if (!invoiceId || !account) return navigate(INVOICES_ROUTE);
        if (!loading) setLoading(true);
        Invoice.get(invoiceId, account.id, (invoice: Invoice | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                if (invoice!.creditInvoice) initializeUpdate(invoice!);
                else initializeNew(invoice!);
            } else
                showDialog(
                    data.message ??
                        createErrorMessage(t('invoice.credit.loadFailed', 'Het laden van de factuur is niet gelukt.')),
                    t('invoice.credit.loadFailedTitle', 'Laden mislukt')
                );
        });
    }

    function initializeNew(invoice: Invoice) {
        setMode(EditMode.NEW);
        const creditInvoice = invoice.createCredit();
        setItems(
            creditInvoice.items.map((item: OrderItem) => {
                return new CreditItem({
                    id: item.id,
                    orderItem: item,
                    percentage: new NumberString(100),
                    priceIncl: new NumberString(item.piecePrice),
                    quantity: new NumberString(item.quantity),
                    vatValue: item.vat
                });
            })
        );
        setCreditInvoice(creditInvoice);
    }

    function initializeUpdate(invoice: Invoice) {
        setMode(EditMode.EDIT);
        const creditInvoice = invoice.creditInvoice!;
        const addedIds: string[] = [];
        const creditItems = creditInvoice.items.map((item: OrderItem) => {
            const origItem = getOrigItem(invoice, item.itemId) || item;
            addedIds.push(origItem.id);
            return new CreditItem({
                id: item.id,
                orderItem: origItem,
                priceIncl: new NumberString(roundTo(item.piecePrice, 2)),
                quantity: new NumberString(item.quantity),
                vatValue: origItem.vat
            });
        });
        for (const origItem of invoice.items) {
            if (addedIds.includes(origItem.id)) continue;
            creditItems.push(
                new CreditItem({
                    id: generateUUID() + '-add',
                    orderItem: origItem,
                    priceIncl: new NumberString(0),
                    quantity: new NumberString(0),
                    vatValue: origItem.vat
                })
            );
        }
        setItems(creditItems);
        setCreditInvoice(creditInvoice);
    }

    function getOrigItem(invoice: Invoice, orderItemId: string): OrderItem | null {
        for (const item of invoice.items) {
            if (item.itemId === orderItemId) return item;
        }
        return null;
    }

    useEffect(() => {
        initialize();
    }, []);

    return (
        <CreditInvoiceData
            onChangePriceIncl={onChangePriceIncl}
            onChangePercentage={onChangePercentage}
            onChangeQuantity={onChangeQuantity}
            onSubmit={onSubmit}
            onClose={onClose}
            items={items}
            invoice={creditInvoice}
            loading={loading}
            onResetError={onResetError}
            errors={errors}
            mode={mode}
            onChangeField={onChangeField}
        />
    );
}
